"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useParams } from "next/navigation";
import { ChevronDown, ShoppingBag } from "lucide-react";
import { useAllCarts } from "@/hooks/use-cart";
import { formatAmount } from "@/lib/money";
import { ulidFromSegment } from "@/lib/seo";

/** Au-delà, la carte deviendrait un écran ; le panier complet est à un clic. */
const PREVIEW_ITEMS = 3;

/**
 * Le panier en cours, lisible et ouvrable.
 *
 * Première version : une pastille vert foncé où le nom du magasin et le montant
 * se lisaient à peine, et qui ne disait rien de son contenu. Reprise en carte,
 * sur les mêmes surfaces que le reste de l'interface — fond blanc ou #1E1E1E,
 * bordure, texte en gris 900 — donc avec le contraste garanti par les mêmes
 * jetons que partout ailleurs, plutôt que du blanc posé sur un aplat de marque.
 *
 * Repliée, elle tient en une ligne : magasin, nombre d'articles, total.
 * Dépliée, elle montre les articles avec leur image et leur prix.
 */
export function FloatingCart() {
  const pathname = usePathname();
  const params = useParams();
  const { data: carts } = useAllCarts();
  const [open, setOpen] = useState(false);

  const storeParam = params?.storeId ?? params?.storeUlid;
  const rawParam = Array.isArray(storeParam) ? storeParam[0] : storeParam;
  // Sur une fiche, l'URL porte le segment slugué (« greenbol--01JX… ») et non
  // l'ULID nu : sans extraction, aucun panier ne correspondait jamais.
  const currentStore = rawParam ? (ulidFromSegment(rawParam) ?? rawParam) : null;

  // Dans le tunnel de commande, la carte n'apprendrait rien.
  const hidden = pathname?.startsWith("/cart") || pathname?.startsWith("/payment");

  const withItems = (carts ?? []).filter((c) => (c.items?.length ?? 0) > 0);
  if (hidden || withItems.length === 0) return null;

  const itemsOf = (c: (typeof withItems)[number]) =>
    c.items?.reduce((n, i) => n + i.quantity, 0) ?? 0;

  const target = withItems.find((c) => c.store_ulid === currentStore) ?? withItems[0];
  const items = target.items ?? [];
  const count = itemsOf(target);
  // Le montant du panier montré, jamais la somme de tous : additionner des
  // paniers de magasins différents afficherait un total qu'on ne paie pas.
  const amount = Number(target.total ?? target.subtotal ?? 0);
  const reste = items.length - PREVIEW_ITEMS;

  return (
    <div className="fixed bottom-20 right-4 z-40 w-[min(21rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl lg:bottom-6 lg:right-6 dark:border-[#313131] dark:bg-[#1E1E1E]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-gray-50 dark:hover:bg-[#262626]"
      >
        <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-white dark:bg-primary dark:text-secondary">
          <ShoppingBag className="h-4 w-4" strokeWidth={2} />
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-white bg-gray-900 px-1 text-[10px] font-bold text-white dark:border-[#1E1E1E] dark:bg-white dark:text-gray-900">
            {count}
          </span>
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-bold text-gray-900 dark:text-white">
            {target.store_name}
          </span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            {count} article{count > 1 ? "s" : ""}
            {amount > 0 && ` · ${formatAmount(amount)} DA`}
            {withItems.length > 1 && ` · ${withItems.length} paniers`}
          </span>
        </span>

        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        <div className="border-t border-gray-100 dark:border-[#313131]">
          <ul className="max-h-56 divide-y divide-gray-100 overflow-y-auto dark:divide-[#313131]">
            {items.slice(0, PREVIEW_ITEMS).map((item) => (
              <li key={item.cart_item_id} className="flex items-center gap-3 px-4 py-2.5">
                <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-gray-100 dark:bg-[#2A2A2A]">
                  {item.product_image && (
                    <Image
                      src={item.product_image}
                      alt=""
                      fill
                      className="object-cover"
                      sizes="36px"
                      unoptimized
                    />
                  )}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-gray-700 dark:text-gray-300">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {item.quantity}&times;
                  </span>{" "}
                  {item.product_name}
                </span>
                <span className="shrink-0 text-[13px] font-semibold text-gray-900 dark:text-white">
                  {formatAmount(item.subtotal)} DA
                </span>
              </li>
            ))}
          </ul>

          {reste > 0 && (
            <p className="px-4 pt-2 text-xs text-gray-500 dark:text-gray-400">
              +{reste} autre{reste > 1 ? "s" : ""} article{reste > 1 ? "s" : ""}
            </p>
          )}

          <div className="p-3">
            <Link
              href={`/cart/${target.store_ulid}`}
              className="block rounded-xl bg-secondary py-2.5 text-center text-sm font-bold text-white transition hover:opacity-90 dark:bg-primary dark:text-secondary"
            >
              Voir le panier
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
